import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { SECTION_PATHS } from '../config/publicNavigation';
import {
  FIVEM_SOLUTION,
  getHomeIndustries,
  getIndustryCommonCopy,
  getLocalizedValue,
} from '../data/industries';
import {
  CONTACT_CONFIG,
  getInstagramLink,
  getTelephoneLink,
  getWhatsAppLink,
} from '../config/contact';
import { Instagram, MessageCircle, Mail, ShieldCheck, Phone } from 'lucide-react';

// Premium W Logo Component with glass/glow effect
const WebouseLogo = ({ size = 48 }) => (
  <div 
    className="relative flex items-center justify-center"
    style={{
      width: size,
      height: size,
      borderRadius: '14px',
      background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.9) 0%, rgba(0, 200, 165, 0.95) 100%)',
      boxShadow: '0 0 20px rgba(0, 255, 209, 0.4), 0 0 40px rgba(0, 255, 209, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.3)',
      backdropFilter: 'blur(8px)',
    }}
  >
    <svg width={size * 0.65} height={size * 0.65} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path 
        d="M3 6L7 20L10.5 12L13 20L16.5 12L20 20L24 6" 
        stroke="#000000" 
        strokeWidth="2"
        strokeLinecap="round" 
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  </div>
);

// Company Information
const COMPANY_INFO = {
  name: 'Webouse Digital Services',
  phone: CONTACT_CONFIG.whatsappDisplay,
  email: CONTACT_CONFIG.supportEmail,
  instagram: CONTACT_CONFIG.instagramDisplay,
};

const legalLinks = [
  { key: 'privacy', path: '/privacy' },
  { key: 'terms', path: '/terms' },
  { key: 'cookies', path: '/cookies' },
];

export const FOOTER_QUICK_LINKS = Object.freeze([
  { key: 'about', path: '/about' },
  { key: 'services', path: '/services' },
  { key: 'work', path: '/work' },
  { key: 'resources', path: '/resources' },
  { key: 'pricing', id: 'pricing' },
  { key: 'modules', id: 'modules' },
  { key: 'plans', id: 'plans' },
  { key: 'contact', id: 'contact' },
  { key: 'bookCall', path: '/book-call' },
]);

const Footer = () => {
  const { t, currentLang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const industryCopy = getIndustryCommonCopy(currentLang);
  const industryLinks = getHomeIndustries().map((industry) =>
    getLocalizedValue(industry, currentLang)
  );
  const fivem = getLocalizedValue(FIVEM_SOLUTION, currentLang);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(SECTION_PATHS[id] || '/');
    }
  };

  return (
    <footer
      className="relative bg-[#0a0a0a] border-t border-white/10"
      style={{ direction: isRTL ? 'rtl' : 'ltr' }}
    >
      <div className="max-w-[1400px] mx-auto px-5 pt-16 pb-28 md:px-[7.6923%] md:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_1fr_1fr] lg:gap-8">
          {/* Brand */}
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <WebouseLogo size={48} />
              <div>
                <span className="text-white font-semibold text-xl block">Webouse</span>
                <span className="text-white/40 text-sm">{t('footer.tagline')}</span>
              </div>
            </div>
            <p className="text-white/60 max-w-md leading-relaxed">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-4">
              <a
                href={getWhatsAppLink(t('footer.whatsappQuestion'))}
                target="_blank"
                rel="noopener noreferrer"
                className="glass-icon-btn"
              >
                <MessageCircle size={20} />
              </a>
              <a
                href={`mailto:${CONTACT_CONFIG.supportEmail}`}
                className="glass-icon-btn"
              >
                <Mail size={20} />
              </a>
              <a
                href={getInstagramLink()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t('common.instagram')}
                className="glass-icon-btn"
              >
                <Instagram size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {FOOTER_QUICK_LINKS.map((link) => (
                <li key={link.path || link.id}>
                  {link.path ? (
                    <Link
                      to={link.path}
                      className="text-white/60 hover:text-[#00FFD1] transition-colors"
                    >
                      {t(`nav.${link.key}`)}
                    </Link>
                  ) : (
                    <button
                      onClick={() => scrollToSection(link.id)}
                      className="text-white/60 hover:text-[#00FFD1] transition-colors"
                    >
                      {t(`nav.${link.key}`)}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h4 className="mb-6 font-semibold text-white">{industryCopy.navLabel}</h4>
            <ul className="space-y-3">
              <li>
                <Link
                  to="/industries"
                  className="text-white/60 hover:text-[#00FFD1] transition-colors"
                >
                  {industryCopy.allIndustries}
                </Link>
              </li>
              {industryLinks.map((industry) => (
                <li key={industry.id}>
                  <Link
                    to={industry.route}
                    className="text-white/60 hover:text-[#00FFD1] transition-colors"
                  >
                    {industry.card.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to={fivem.route}
                  className="text-[#00FFD1] hover:text-white transition-colors"
                >
                  {fivem.card.title}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-6">{t('footer.contactUs')}</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={getTelephoneLink()}
                  className="text-white/60 hover:text-[#00FFD1] transition-colors flex items-center gap-3"
                >
                  <Phone size={18} className="text-[#00FFD1]" />
                  <span className="text-sm">{COMPANY_INFO.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="text-white/60 hover:text-[#00FFD1] transition-colors flex items-center gap-3"
                >
                  <Mail size={18} className="text-[#00FFD1]" />
                  <span className="text-sm">{COMPANY_INFO.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-[#00FFD1] transition-colors flex items-center gap-3"
                >
                  <MessageCircle size={18} className="text-[#00FFD1]" />
                  <span className="text-sm">WhatsApp</span>
                </a>
              </li>
              <li>
                <a
                  href={getInstagramLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 hover:text-[#00FFD1] transition-colors flex items-center gap-3"
                >
                  <Instagram size={18} className="text-[#00FFD1]" />
                  <span className="text-sm">{COMPANY_INFO.instagram}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Company Info Bar */}
        <div className="mt-12 p-6 rounded-xl" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className={`text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
              <p className="text-white font-medium">{COMPANY_INFO.name}</p>
              <p className="text-white/50 text-sm mt-1">{t('footer.worldwide')}</p>
            </div>
            <div className="flex items-center gap-6 text-white/50 text-sm">
              <span>{COMPANY_INFO.phone}</span>
              <span>{COMPANY_INFO.email}</span>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/40 text-sm">{t('footer.copyright')}</p>
          <div className="flex flex-col items-center gap-3 text-white/40 text-sm md:flex-row md:gap-5">
            <div className="flex flex-wrap items-center justify-center gap-4">
              {legalLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="hover:text-[#00FFD1] transition-colors"
                >
                  {t(`common.${link.key}`)}
                </Link>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#00FFD1]" />
              <span>{t('footer.secure')}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
